import React, { useEffect, useMemo, useState } from 'react';
import styles from './MarketTemplates.module.scss';
import Market from './Market/Market';
import { useSelector } from 'react-redux';
import { LANGUAGES, MARKET_GROUPS_ALL_ID } from '../../../../constants/defaultValues';
import { formatSpecifierName, formatTeamNames } from '../../../../utils/render';
import { getTranslation } from '../../../../utils/translation';
import Mbn from '../../../shared/Mbn/Mbn';
import { ReactComponent as NewMarketIcon } from '../../../../assets/icons/new-market.svg';
import {
    filterTemplatesByMarketStatus,
    filterTemplatesByMarketType,
    filterVisibleMarkets,
    findUnConfiguredMarkets,
    generateTemplateColumns,
    mergeDuplicateTemplateResultTypes,
} from '../../../../utils/market-groups';

const COLUMN_COUNT = 2;

const MarketTemplates = props => {
    const marketGroups = useSelector(state => state.allMarkets.filteredMarketGroups);
    const selectedGroupId = useSelector(state => state.allMarkets.selectedMarketGroup);
    const isMarketsCollapsed = useSelector(state => state.allMarkets.isMarketsCollapsed);
    const locale = useSelector(state => state.settings.locale);

    const { markets, teams, tournamentName, playerNames } = props;

    const [collapsedAccordionItems, setCollapsedAccordionItems] = useState([]);

    const formattedTeamNames = formatTeamNames(teams, locale);

    const selectedMarketGroup = marketGroups.find(group => group.id === selectedGroupId);

    const { templateColumns, unConfiguredMarkets } = useMemo(() => {
        const groupTemplates =
            selectedGroupId === MARKET_GROUPS_ALL_ID
                ? marketGroups.reduce((result, group) => {
                      const groupTemplates = group.templateList || [];
                      return [...result, ...groupTemplates];
                  }, [])
                : selectedMarketGroup?.templateList || [];

        const templates = mergeDuplicateTemplateResultTypes(groupTemplates);

        const filteredTemplates = filterTemplatesByMarketType({
            templates: filterTemplatesByMarketStatus({
                templates,
                markets,
            }),
            markets,
        });

        const templateColumns = generateTemplateColumns({
            templates: filteredTemplates,
            columnCount: COLUMN_COUNT,
        });

        const unConfiguredMarkets =
            selectedGroupId === MARKET_GROUPS_ALL_ID
                ? findUnConfiguredMarkets({ templates, markets })
                : [];

        return {
            templateColumns,
            unConfiguredMarkets,
        };
    }, [selectedMarketGroup, marketGroups, markets]);

    const onCollapseChange = (id, isCollapsed) => {
        let newCollapsedItems = [];

        if (isCollapsed) {
            newCollapsedItems = collapsedAccordionItems.filter(item => item !== id);
        } else {
            newCollapsedItems = collapsedAccordionItems.concat(id);
        }

        setCollapsedAccordionItems(newCollapsedItems);
    };

    useEffect(() => {
        const collapsedAccordionItems = [];

        if (isMarketsCollapsed) {
            Object.values(markets).forEach(marketRefGroup => {
                marketRefGroup.forEach(market => {
                    collapsedAccordionItems.push(market.id);
                });
            });
            marketGroups
                .reduce((result, group) => {
                    if (group.templateList) {
                        return [...result, ...group.templateList];
                    } else {
                        return result;
                    }
                }, [])
                .forEach(template => {
                    collapsedAccordionItems.push(template.id);
                });
        }

        setCollapsedAccordionItems(collapsedAccordionItems);
    }, [isMarketsCollapsed, marketGroups]);

    const renderTemplates = templates => {
        return (
            <div>
                {templates.map(template => {
                    const { marketType } = template;
                    const isCollapsed = collapsedAccordionItems.find(item => item === template.id);

                    const allResultTypeMarkets = template.resultTypes
                        .map(resultType => {
                            return filterVisibleMarkets({
                                markets: markets[resultType.marketRefId],
                                marketType,
                            });
                        })
                        .reduce(
                            (allMarkets, resultTypeMarkets) => [
                                ...allMarkets,
                                ...resultTypeMarkets,
                            ],
                            [],
                        );
                    const templateMbns = [
                        ...new Set(allResultTypeMarkets.map(market => market.mbn)),
                    ];

                    return (
                        allResultTypeMarkets.length > 0 && (
                            <div key={template.id} className={styles.accordionItem}>
                                <div
                                    className={styles.accordionHeader}
                                    onClick={() => onCollapseChange(template.id, isCollapsed)}
                                >
                                    <div className={styles.marketInfoContainer}>
                                        <span>{template.name}</span>
                                        {templateMbns.map(mbn => (
                                            <Mbn
                                                key={new Date().getTime() * Math.random()}
                                                mbn={mbn}
                                            />
                                        ))}
                                    </div>
                                    <div className='flex'>
                                        {template.featured ? (
                                            <div className='m-r-2'>
                                                <div className="relative flex justify-center items-center">
                                                    <NewMarketIcon />
                                                    <span className="absolute text-sm font-bold" style={{ color: 'white' }}>
                                                        {getTranslation('newMarketIcon')}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : null}
                                        <span>
                                            <span
                                                className={`${styles.accordionArrowIcon} icon-arrow-down`}
                                            />
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className={`${styles.accordionContent}  ${isCollapsed ? 'd-hidden' : ''}`}
                                >
                                    {template.resultTypes.map(resultType => {
                                        const foundMarkets = markets[resultType.marketRefId];
                                        const filteredMarkets = filterVisibleMarkets({
                                            markets: foundMarkets,
                                            marketType,
                                        });

                                        return (
                                            filteredMarkets.length > 0 && (
                                                <Market
                                                    key={resultType.resultTypeId}
                                                    market={filteredMarkets}
                                                    resultType={resultType}
                                                    teams={formattedTeamNames}
                                                    tournamentName={tournamentName}
                                                    playerNames={playerNames}
                                                />
                                            )
                                        );
                                    })}
                                </div>
                            </div>
                        )
                    );
                })}
            </div>
        );
    };

    const totalMarketsCount =
        Object.values(templateColumns).reduce((total, column) => total + column.length, 0) +
        unConfiguredMarkets.length;

    return (
        <div className={styles.accordion}>
            {totalMarketsCount === 0 ? (
                <div className={styles.empty}>{getTranslation('noMarketsAvailable')}</div>
            ) : (
                <>
                    {Object.values(templateColumns).map(templateColumn => (
                        <div
                            key={templateColumn.id}
                            style={{ width: `${100 / COLUMN_COUNT - 1}%` }}
                        >
                            {renderTemplates(templateColumn.templates)}
                        </div>
                    ))}
                    {filterVisibleMarkets({ markets: unConfiguredMarkets }).map(market => {
                        const isCollapsed = collapsedAccordionItems.find(
                            item => item === market.id,
                        );

                        return (
                            <div key={market.id} className={styles.singleMarketColumn}>
                                <div key={market.id} className={styles.accordionItem}>
                                    <div
                                        className={styles.accordionHeader}
                                        onClick={() => onCollapseChange(market.id, isCollapsed)}
                                    >
                                        <div className={styles.marketInfoContainer}>
                                            <span>
                                                {formatSpecifierName({
                                                    name:
                                                        market.names[locale] ||
                                                        market.names[LANGUAGES.eng],
                                                    specifiers: market.specifiers,
                                                    teams: formattedTeamNames,
                                                    tournamentName,
                                                    playerNames,
                                                })}
                                            </span>
                                            <Mbn mbn={market.mbn} />
                                        </div>
                                        <span
                                            className={`${styles.accordionArrowIcon} icon-arrow-down`}
                                        />
                                    </div>
                                    <div
                                        className={`${styles.accordionContent} ${isCollapsed ? 'd-hidden' : ''}`}
                                    >
                                        <Market
                                            market={[market]}
                                            resultType={{ name: '' }}
                                            teams={formattedTeamNames}
                                            tournamentName={tournamentName}
                                            playerNames={playerNames}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};

export default MarketTemplates;
